import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  tenantManagementApi,
  type RegisterDomainResponse,
} from '../../../api/tenantManagement';
import { resolveHostname, updateStorefront } from '../../../api/storefront';
import { validateDomain } from '../../../schemas/domain.schema';

// Re-export for convenience
export { domainSchema, validateDomain } from '../../../schemas/domain.schema';
export type { DomainValidationResult } from '../../../schemas/domain.schema';

interface CheckDomainExistsParams {
  domain: string;
  tenantId: string;
}

interface CheckDomainExistsResult {
  exists: boolean;
  belongsToCurrentTenant: boolean;
}

/**
 * Check if a domain is already taken — resolved by the Storefront service
 * (Spanner DomainMappings / Organizations.domain), not Firestore.
 */
export const useCheckDomainExists = () => {
  return useMutation({
    mutationFn: async ({
      domain,
      tenantId,
    }: CheckDomainExistsParams): Promise<CheckDomainExistsResult> => {
      const res = await resolveHostname(domain);
      return {
        exists: res.exists,
        belongsToCurrentTenant: res.exists && res.tenantId === tenantId,
      };
    },
  });
};

interface ChangeDomainParams {
  oldDomain: string;
  newDomain: string;
  organizationId: string;
}

interface ChangeDomainResult {
  success: boolean;
  domainRegistrationResult?: RegisterDomainResponse;
}

/**
 * Change domain:
 * 1. Validate format
 * 2. Check the new domain isn't owned by another org (Storefront resolve)
 * 3. Register in Firebase Auth + Hosting (delegated infra — tenantManagement)
 * 4. Persist the domain on the tenant via the Storefront service (Spanner)
 */
export const useChangeDomain = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      oldDomain,
      newDomain,
      organizationId,
    }: ChangeDomainParams): Promise<ChangeDomainResult> => {
      // Step 1: Validate domain format
      const validation = validateDomain(newDomain);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Step 2: Ownership check via Storefront resolve (no Firestore)
      if (newDomain !== oldDomain) {
        const res = await resolveHostname(newDomain);
        if (res.exists && res.tenantId !== organizationId) {
          throw new Error(`Domain "${newDomain}" is already registered to another organization.`);
        }
      }

      // Step 3: Register new domain in Firebase Auth + Hosting FIRST (delegated infra).
      // If this fails we don't persist anything.
      let domainRegistrationResult;
      try {
        domainRegistrationResult = await tenantManagementApi.registerDomain({
          organisation_id: organizationId,
          domain: newDomain,
        });

        // Backend returns 207 with success:false when Firebase APIs fail — check explicitly
        if (!domainRegistrationResult.data?.success) {
          const authError = domainRegistrationResult.data?.results?.authentication?.error;
          const hostingError = domainRegistrationResult.data?.results?.hosting?.error;
          throw new Error(authError || hostingError || 'Domain registration failed');
        }
      } catch (domainError) {
        throw new Error(
          `Failed to register domain: ${domainError instanceof Error ? domainError.message : 'Unknown error'}`
        );
      }

      // Step 4: Persist the domain on the tenant via the Storefront service (Spanner).
      // tenant-config resolves hostnames from Organizations.domain, so this is the
      // source of truth for routing; DomainMappings provisioning stays with registerDomain.
      await updateStorefront(organizationId, { domain: { primary: newDomain, aliases: [] } });

      return {
        success: true,
        domainRegistrationResult: domainRegistrationResult?.data,
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.organizationId] });
      if (variables.oldDomain) {
        queryClient.invalidateQueries({ queryKey: ['domain-mapping', variables.oldDomain] });
      }
      queryClient.invalidateQueries({ queryKey: ['domain-mapping', variables.newDomain] });
    },
  });
};
